import { localStateStore } from '@/utils/localStateStore';

interface PrayerTime {
  name: string;
  time: string;
}

interface PrayerSettings {
  enabled?: boolean;
  pauseDuringPrayer: boolean;
  pauseDuration: number;
}

type RawPrayerTimes = PrayerTime[] | Record<string, unknown>;

interface PrayerTimeData {
  prayerTimes: RawPrayerTimes;
  timezone: string;
  lastUpdate: string;
}

interface DailyPrayerTimes {
  date: string;
  [prayer: string]: unknown;
}

const DEFAULT_TIMEZONE = 'Europe/Istanbul';
const DEFAULT_PAUSE_DURATION_MINUTES = 7;
const PRAYER_LEAD_MINUTES = 2;
const MINUTES_PER_DAY = 24 * 60;

const ignoredPrayerKeys = new Set([
  'sunrise',
  'gunes',
  'source',
  'date',
  'timezone',
  'currentTime',
  'lastUpdate'
]);

class PrayerTimeService {
  private static instance: PrayerTimeService;
  private checkInterval: number | null = null;
  private currentPrayerState: {
    isPrayerTime: boolean;
    activePrayerName: string | null;
    prayerEndTime: Date | null;
  } = {
    isPrayerTime: false,
    activePrayerName: null,
    prayerEndTime: null
  };

  private constructor() {
    this.startPrayerTimeCheck();
  }

  static getInstance(): PrayerTimeService {
    if (!PrayerTimeService.instance) {
      PrayerTimeService.instance = new PrayerTimeService();
    }
    return PrayerTimeService.instance;
  }

  savePrayerTimes(data: any) {
    const prayerTimeData: PrayerTimeData = {
      prayerTimes: data?.times || data?.prayerTimes || data?.data?.times || data,
      timezone: data?.timezone || data?.data?.timezone || DEFAULT_TIMEZONE,
      lastUpdate: new Date().toISOString()
    };

    localStateStore.setJson('prayerTimes', prayerTimeData);
    window.dispatchEvent(new CustomEvent('prayerTimesUpdated', {
      detail: prayerTimeData
    }));

    this.checkPrayerTimes();
  }

  /**
   * Haftalık namaz vakti dizisini kaydet ve bugüne ait olanı aktif hale getir.
   * Sekme/uygulama bağlı kaldığı sürece (sunucudan yeni mesaj gelmeden) gün
   * değiştikçe de doğru günün vaktini kullanabilmesi için saklanıyor.
   */
  saveWeeklyPrayerTimes(data: any) {
    const days: DailyPrayerTimes[] = Array.isArray(data?.days) ? data.days : [];
    if (days.length === 0) return;

    localStateStore.setJson('weeklyPrayerTimes', days);

    const today = this.getLocalDateString();
    const todayEntry = days.find((entry) => entry.date === today);
    if (todayEntry) {
      this.savePrayerTimes({ times: todayEntry, timezone: data?.timezone });
    }
  }

  /**
   * Cihazın kendi yerel saatine göre bugünün tarihini "YYYY-MM-DD" olarak döndürür.
   */
  private getLocalDateString(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Saklanan haftalık diziden bugüne ait kayıt varsa ve aktif olarak
   * kullanılan tek günlük veri farklıysa (gün değişmiş demektir), sunucudan
   * yeni mesaj gelmeden de sessizce günceller. checkPrayerTimes() içinden
   * çağrıldığı için savePrayerTimes()'ı (yeniden checkPrayerTimes tetikler)
   * değil, doğrudan depolamayı günceller.
   */
  private refreshTodayFromWeeklyIfNeeded(): void {
    const weekly = localStateStore.getJson<DailyPrayerTimes[]>('weeklyPrayerTimes', null);
    if (!Array.isArray(weekly) || weekly.length === 0) return;

    const today = this.getLocalDateString();
    const todayEntry = weekly.find((entry) => entry.date === today);
    if (!todayEntry) return;

    const current = localStateStore.getJson<PrayerTimeData>('prayerTimes', null);
    const currentDate = (current?.prayerTimes as DailyPrayerTimes | undefined)?.date;
    if (currentDate === today) return;

    const prayerTimeData: PrayerTimeData = {
      prayerTimes: todayEntry,
      timezone: current?.timezone || DEFAULT_TIMEZONE,
      lastUpdate: new Date().toISOString()
    };

    localStateStore.setJson('prayerTimes', prayerTimeData);
    window.dispatchEvent(new CustomEvent('prayerTimesUpdated', {
      detail: prayerTimeData
    }));
  }

  savePrayerSettings(settings: any) {
    const pauseDuringPrayer = Boolean(settings?.pauseDuringPrayer ?? settings?.enabled ?? false);
    const prayerSettings: PrayerSettings = {
      enabled: Boolean(settings?.enabled ?? pauseDuringPrayer),
      pauseDuringPrayer,
      pauseDuration: this.normalizePauseDuration(settings?.pauseDuration)
    };

    localStateStore.setJson('prayerSettings', prayerSettings);
    window.dispatchEvent(new CustomEvent('prayerSettingsUpdated', {
      detail: prayerSettings
    }));

    this.checkPrayerTimes();
  }

  private normalizePauseDuration(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return DEFAULT_PAUSE_DURATION_MINUTES;
    }

    return Math.min(120, Math.max(1, parsed));
  }

  private startPrayerTimeCheck() {
    if (this.checkInterval) {
      window.clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.checkPrayerTimes();
    this.checkInterval = window.setInterval(() => {
      this.checkPrayerTimes();
    }, 30000);
  }

  private getCurrentMinutesInTimezone(timezone: string): number {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone || DEFAULT_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(new Date());

      const partMap = new Map(parts.map(part => [part.type, part.value]));
      const hours = Number(partMap.get('hour') || 0) % 24;
      const minutes = Number(partMap.get('minute') || 0);
      const seconds = Number(partMap.get('second') || 0);

      if ([hours, minutes, seconds].every(Number.isFinite)) {
        return hours * 60 + minutes + seconds / 60;
      }
    } catch {
      // Invalid timezone data should not stop playback scheduling.
    }

    const now = new Date();
    return now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  }

  private parseTimeToMinutes(value: unknown): number | null {
    const match = String(value || '').match(/^(\d{1,2}):(\d{2})/);
    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (
      !Number.isInteger(hours) ||
      !Number.isInteger(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    return hours * 60 + minutes;
  }

  private normalizeKey(key: string): string {
    return key
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ı/g, 'i');
  }

  private normalizePrayerName(key: string): string {
    const names: Record<string, string> = {
      fajr: 'Sabah',
      imsak: 'Sabah',
      sabah: 'Sabah',
      dhuhr: 'Ogle',
      ogle: 'Ogle',
      asr: 'Ikindi',
      ikindi: 'Ikindi',
      maghrib: 'Aksam',
      aksam: 'Aksam',
      isha: 'Yatsi',
      yatsi: 'Yatsi'
    };

    return names[this.normalizeKey(key)] || key;
  }

  private normalizePrayerTimes(rawPrayerTimes: RawPrayerTimes): PrayerTime[] {
    if (Array.isArray(rawPrayerTimes)) {
      return rawPrayerTimes
        .map((prayer) => {
          const time = this.parseTimeToMinutes(prayer?.time);
          if (time === null) {
            return null;
          }

          return {
            name: prayer.name || 'Ezan',
            time: prayer.time
          };
        })
        .filter((prayer): prayer is PrayerTime => Boolean(prayer));
    }

    if (!rawPrayerTimes || typeof rawPrayerTimes !== 'object') {
      return [];
    }

    return Object.entries(rawPrayerTimes)
      .filter(([key]) => !ignoredPrayerKeys.has(this.normalizeKey(key)))
      .map(([key, value]) => {
        const time = this.parseTimeToMinutes(value);
        if (time === null) {
          return null;
        }

        return {
          name: this.normalizePrayerName(key),
          time: String(value)
        };
      })
      .filter((prayer): prayer is PrayerTime => Boolean(prayer));
  }

  private normalizeMinuteDelta(value: number): number {
    return ((value % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  }

  private getActivePrayerWindow(
    currentMinutes: number,
    prayerMinutes: number,
    pauseDuration: number
  ): { remainingMinutes: number } | null {
    const windowLength = PRAYER_LEAD_MINUTES + pauseDuration;
    const windowStart = prayerMinutes - PRAYER_LEAD_MINUTES;
    const elapsed = this.normalizeMinuteDelta(currentMinutes - windowStart);

    if (elapsed <= windowLength) {
      return {
        remainingMinutes: Math.max(0, windowLength - elapsed)
      };
    }

    return null;
  }

  private checkPrayerTimes() {
    try {
      const settings = localStateStore.getJson<PrayerSettings>('prayerSettings', null);
      if (!settings) {
        return;
      }

      if (!settings.pauseDuringPrayer) {
        if (this.currentPrayerState.isPrayerTime) {
          this.endPrayerTime();
        }
        return;
      }

      this.refreshTodayFromWeeklyIfNeeded();

      const data = localStateStore.getJson<PrayerTimeData>('prayerTimes', null);
      if (!data?.prayerTimes) {
        return;
      }

      const prayerTimes = this.normalizePrayerTimes(data.prayerTimes);
      if (prayerTimes.length === 0) {
        return;
      }

      const currentMinutes = this.getCurrentMinutesInTimezone(data.timezone || DEFAULT_TIMEZONE);
      const pauseDuration = this.normalizePauseDuration(settings.pauseDuration);

      for (const prayer of prayerTimes) {
        const prayerMinutes = this.parseTimeToMinutes(prayer.time);
        if (prayerMinutes === null) {
          continue;
        }

        const activeWindow = this.getActivePrayerWindow(currentMinutes, prayerMinutes, pauseDuration);
        if (!activeWindow) {
          continue;
        }

        const endTime = new Date(Date.now() + activeWindow.remainingMinutes * 60000);
        if (
          !this.currentPrayerState.isPrayerTime ||
          this.currentPrayerState.activePrayerName !== prayer.name
        ) {
          this.startPrayerTime(prayer.name, endTime);
        } else {
          this.currentPrayerState.prayerEndTime = endTime;
        }
        return;
      }

      if (this.currentPrayerState.isPrayerTime) {
        this.endPrayerTime();
      }
    } catch (error) {
      console.error('[PrayerTime] Ezan vakti kontrol hatasi:', error);
    }
  }

  private startPrayerTime(prayerName: string, endTime: Date) {
    this.currentPrayerState = {
      isPrayerTime: true,
      activePrayerName: prayerName,
      prayerEndTime: endTime
    };

    window.dispatchEvent(new CustomEvent('prayerTimeStarted', {
      detail: {
        prayerName,
        endTime
      }
    }));
  }

  private endPrayerTime() {
    const previousPrayerName = this.currentPrayerState.activePrayerName;

    this.currentPrayerState = {
      isPrayerTime: false,
      activePrayerName: null,
      prayerEndTime: null
    };

    window.dispatchEvent(new CustomEvent('prayerTimeEnded', {
      detail: {
        previousPrayerName
      }
    }));
  }

  getCurrentState() {
    return { ...this.currentPrayerState };
  }

  cleanup() {
    if (this.checkInterval) {
      window.clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }
}

export default PrayerTimeService.getInstance();
